import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { setConsent } from "../cookieConsentSlice";
import { closeGlobalModal } from "../../globalModal/globalModalSlice";
import { ConsentState, DEFAULT_CONSENT } from "../types";

const CookiePreferencesModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { currentConsent, hasUserConsented } = useAppSelector(
    (state) => state.cookieConsent
  );

  // Default state: If previously consented, use that. If new user, use DEFAULT (False).
  const initialState = hasUserConsented ? currentConsent : DEFAULT_CONSENT;

  const [preferences, setPreferences] = useState<ConsentState>(initialState);

  const handleToggle = (key: keyof ConsentState) => {
    if (key === "necessary") return;
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = () => {
    const timestamp = new Date().toISOString();

    // Revocation check
    let requiresReload = false;
    if (currentConsent.analytics && !preferences.analytics)
      requiresReload = true;
    if (currentConsent.marketing && !preferences.marketing)
      requiresReload = true;

    dispatch(
      setConsent({
        ...preferences,
        timestamp,
      })
    );
    dispatch(closeGlobalModal());

    if (requiresReload) {
      window.location.reload();
    }
  };

  return (
    <div className="cookie-preferences">
      <p className="cookie-preferences__description">
        {t(
          "cookieConsent.preferencesDescription",
          "You can customize your cookie preferences below. Necessary cookies are always enabled to ensure the website functions correctly."
        )}
      </p>

      <div className="cookie-preferences__list">
        {/* Necessary (Always Disabled Input, Checked) */}
        <div className="cookie-preference-item">
          <div className="cookie-preference-item__info">
            <span className="cookie-preference-item__title">
              {t("cookieConsent.category.necessary", "Necessary")}
            </span>
            <span className="cookie-preference-item__desc">
              {t(
                "cookieConsent.category.necessaryDesc",
                "Required for the website to function."
              )}
            </span>
          </div>
          <div className="cookie-preference-item__control">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={true}
                disabled
                className="toggle-switch"
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Analytics */}
        <div className="cookie-preference-item">
          <div className="cookie-preference-item__info">
            <span className="cookie-preference-item__title">
              {t("cookieConsent.category.analytics", "Analytics")}
            </span>
            <span className="cookie-preference-item__desc">
              {t("cookieConsent.category.analyticsDesc", "Google Analytics")}
            </span>
          </div>
          <div className="cookie-preference-item__control">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={preferences.analytics}
                onChange={() => handleToggle("analytics")}
                className="toggle-switch"
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Marketing */}
        <div className="cookie-preference-item">
          <div className="cookie-preference-item__info">
            <span className="cookie-preference-item__title">
              {t("cookieConsent.category.marketing", "Marketing")}
            </span>
            <span className="cookie-preference-item__desc">
              {t("cookieConsent.category.marketingDesc", "Meta Pixel, Ads")}
            </span>
          </div>
          <div className="cookie-preference-item__control">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={preferences.marketing}
                onChange={() => handleToggle("marketing")}
                className="toggle-switch"
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      <div className="cookie-preferences__actions">
        <button className="cookie-preferences__save-btn" onClick={handleSave}>
          {t("cookieConsent.savePreferences", "Save Preferences")}
        </button>
      </div>
    </div>
  );
};

export default CookiePreferencesModal;
